import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Trash2, Link, IndianRupee, RefreshCw, ToggleLeft, ToggleRight, Save, X, FileText } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import './Bundles.css';

// Available pages for bundle selection
const PAGE_OPTIONS = [
  { value: 'all', label: 'All Pages' },
  { value: 'blocked-payments', label: 'Home Page (/)' },
  { value: 'prbk', label: 'PRBK (/prbk)' },
  { value: 'credit-control', label: 'Credit Control (/credit-control)' },
];

const Bundles = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    link: '',
    pages: 'all'
  });

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payments/bundles');
      if (response.data.success) {
        setBundles(response.data.bundles);
      }
    } catch (error) {
      toast.error('Failed to fetch bundles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', link: '', pages: 'all' });
    setShowAddForm(false);
    setEditingBundle(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      const response = await api.post('/payments/bundles', {
        name: formData.name,
        price: parseFloat(formData.price),
        link: formData.link,
        pages: formData.pages
      });

      if (response.data.success) {
        toast.success('Bundle added successfully');
        fetchBundles();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add bundle');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      const response = await api.put(`/payments/bundles/${editingBundle.id}`, {
        name: formData.name,
        price: parseFloat(formData.price),
        link: formData.link,
        pages: formData.pages,
        is_active: editingBundle.is_active
      });

      if (response.data.success) {
        toast.success('Bundle updated successfully');
        fetchBundles();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update bundle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bundle?')) return;

    try {
      const response = await api.delete(`/payments/bundles/${id}`);
      if (response.data.success) {
        toast.success('Bundle deleted successfully');
        fetchBundles();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete bundle');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await api.patch(`/payments/bundles/${id}/toggle`);
      if (response.data.success) {
        toast.success('Bundle status updated');
        fetchBundles();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to toggle bundle');
    }
  };

  const startEdit = (bundle) => {
    setEditingBundle(bundle);
    setFormData({
      name: bundle.name,
      price: bundle.price.toString(),
      link: bundle.link || '',
      pages: bundle.pages || 'all'
    });
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <RefreshCw className="spin" size={32} />
        <p>Loading bundles...</p>
      </div>
    );
  }

  return (
    <div className="bundles-page">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <Package size={28} />
            Bundle Management
          </h1>
          <p>Add, edit, or remove bundles shown in payment approval</p>
        </div>
        <button 
          className="btn btn-primary btn-add-bundle"
          onClick={() => {
            resetForm();
            setShowAddForm(true);
          }}
        >
          <Plus size={18} />
          Add Bundle
        </button>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingBundle) && (
        <div className="card bundle-form-card">
          <div className="card-header">
            <h3>{editingBundle ? 'Edit Bundle' : 'Add New Bundle'}</h3>
            <button className="btn-close" onClick={resetForm}>
              <X size={18} />
            </button>
          </div>
          <form onSubmit={editingBundle ? handleUpdate : handleAdd}>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <Package size={16} />
                  Bundle Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Blocked Payments Recovery Kit"
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <IndianRupee size={16} />
                  Price (₹) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="e.g., 4000"
                  min="0"
                  required
                />
              </div>
              <div className="form-group form-group-full">
                <label>
                  <Link size={16} />
                  Access Link (optional)
                </label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  placeholder="e.g., https://lp1.rushneeksingh.com/blocked-payments"
                />
              </div>
              <div className="form-group form-group-full">
                <label>
                  <FileText size={16} />
                  Show on Pages *
                </label>
                <select
                  name="pages"
                  value={formData.pages}
                  onChange={handleInputChange}
                  className="page-select"
                >
                  {PAGE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="form-help">Select which page(s) this bundle should appear on</small>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                {editingBundle ? 'Update Bundle' : 'Add Bundle'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bundles List */}
      <div className="bundles-grid">
        {bundles.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <h3>No Bundles Yet</h3>
            <p>Add your first bundle to show in payment approval</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <Plus size={18} />
              Add First Bundle
            </button>
          </div>
        ) : (
          bundles.map(bundle => (
            <div key={bundle.id} className={`bundle-card ${!bundle.is_active ? 'inactive' : ''}`}>
              <div className="bundle-header">
                <div className="bundle-status">
                  <button 
                    className={`toggle-btn ${bundle.is_active ? 'active' : ''}`}
                    onClick={() => handleToggle(bundle.id)}
                    title={bundle.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {bundle.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                  <span className={`status-text ${bundle.is_active ? 'active' : 'inactive'}`}>
                    {bundle.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="bundle-actions">
                  <button 
                    className="btn-icon edit"
                    onClick={() => startEdit(bundle)}
                    title="Edit bundle"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(bundle.id)}
                    title="Delete bundle"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="bundle-body">
                <h3 className="bundle-name">{bundle.name}</h3>
                <div className="bundle-price">
                  <IndianRupee size={18} />
                  <span>{parseFloat(bundle.price).toLocaleString('en-IN')}</span>
                </div>
                <div className="bundle-pages">
                  <FileText size={14} />
                  <span>{PAGE_OPTIONS.find(p => p.value === bundle.pages)?.label || 'All Pages'}</span>
                </div>
                {bundle.link && (
                  <a href={bundle.link} target="_blank" rel="noopener noreferrer" className="bundle-link">
                    <Link size={14} />
                    {bundle.link}
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Bundles;
